import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import { capitalizeName } from "@/lib/formatName";
import { useAvatarSnapshot } from "@/hooks/useAvatarSnapshot";
import { useFieldDictation } from "@/hooks/useFieldDictation";
import { saveProfilePreference } from "@/lib/settingsPreferences";

const SettingsProfile = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { snapshotUrl, hasAvatar } = useAvatarSnapshot();
  const [name, setName] = useState("");
  const [savedName, setSavedName] = useState("");
  const [goal, setGoal] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const owner = useRef<string | null>(null);
  const busy = useRef(false);
  const dictation = useFieldDictation();

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    setLoaded(false); setError(false); owner.current = null;
    void (async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) throw authError || new Error("No session");
        if (!active) return;
        owner.current = user.id;
        const { data, error: loadError } = await supabase.from("user_profiles")
          .select("first_name, primary_goal").eq("user_id", user.id).abortSignal(controller.signal).single();
        if (loadError || !data) throw loadError || new Error("No profile");
        if (!active || owner.current !== user.id) return;
        setName(data.first_name || ""); setSavedName(data.first_name || "");
        setGoal(data.primary_goal || ""); setLoaded(true);
      } catch { if (active) setError(true); }
      finally { window.clearTimeout(timeout); }
    })();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (owner.current && owner.current !== session?.user.id) {
        owner.current = null; setName(""); setSavedName(""); setGoal(""); setLoaded(false);
        setAttempt(value => value + 1);
      }
    });
    return () => { active = false; owner.current = null; controller.abort(); window.clearTimeout(timeout); subscription.unsubscribe(); };
  }, [attempt]);

  const saveName = async () => {
    const id = owner.current;
    if (!id || !loaded || busy.current || !name.trim()) return;
    const cleanName = capitalizeName(name.trim());
    busy.current = true; setSaving(true);
    try {
      await saveProfilePreference(id, "first_name", cleanName);
      if (owner.current !== id) return;
      setName(cleanName); setSavedName(cleanName); toast.success(t("settingsProfile.saved"));
    } catch { if (owner.current === id) toast.error(t("settingsProfile.saveError")); }
    finally { busy.current = false; setSaving(false); }
  };

  return <main className="min-h-screen max-w-xl mx-auto bg-background text-foreground px-5 pb-16"
    style={{ paddingTop: "max(env(safe-area-inset-top, 0px) + 1rem, 2rem)" }}>
    <button type="button" onClick={() => navigate(-1)} className="min-h-11 flex items-center gap-1 text-sm text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary rounded-lg">
      <ChevronLeft size={18} />{t("common.back")}
    </button>
    <h1 className="mt-4 text-2xl font-display font-semibold">{t("settingsProfile.title")}</h1>
    <div className="mt-7 mb-8 w-20 h-20 rounded-full overflow-hidden grid place-items-center bg-primary/10 border border-primary/30 text-3xl text-primary">
      {hasAvatar && snapshotUrl ? <img src={snapshotUrl} alt={savedName || t("common.user")} className="w-full h-full object-cover" /> : (savedName.charAt(0).toUpperCase() || "?")}
    </div>
    {error && <div role="alert" className="mb-6 p-4 rounded-xl border border-amber-400/40 text-sm">
      <p>{t("settings.ux.loadError")}</p>
      <button type="button" onClick={() => setAttempt(value => value + 1)} className="min-h-11 font-semibold text-primary">{t("common.retry")}</button>
    </div>}
    {!loaded && !error && <p role="status">{t("common.loading")}</p>}
    <form onSubmit={event => { event.preventDefault(); void saveName(); }} className="space-y-5">
      <div>
        <label htmlFor="profile-name" className="block mb-2 text-sm font-medium">{t("settingsProfile.name")}</label>
        <input id="profile-name" autoComplete="given-name" maxLength={80} value={name} disabled={!loaded || saving || dictation.active}
          onChange={event => setName(event.target.value)} className="w-full rounded-xl border border-border bg-card px-4 py-3 text-base focus-visible:outline-primary disabled:opacity-50" />
        {loaded && !saving && dictation.button(t("settingsProfile.name"), text => setName(text.slice(0, 80)))}
      </div>
      {goal && <div><p className="mb-2 text-sm font-medium">{t("settingsProfile.goal")}</p><p className="text-muted-foreground">{t(`goal.${goal}`)}</p></div>}
      <button type="submit" disabled={!loaded || saving || dictation.active || !name.trim() || name.trim() === savedName}
        className="w-full min-h-12 rounded-xl bg-primary text-primary-foreground font-semibold disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
        {t(saving ? "settings.ux.saving" : "settingsProfile.save")}
      </button>
    </form>
  </main>;
};
export default SettingsProfile;
